// src/features/rfp/RFPBar.tsx
import React, { useEffect, useMemo } from "react";
import { Group, Text, Divider, Button, Tooltip } from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectStandardRoomValidationColorItems,
  selectValidationDecisionColorItems,
} from "../../Room/roomSelectors";
import { setActiveColorPanelItemInRedux } from "../designSlice";
import { ModuleToolBarShell } from "./ModuleToolBarShell";

type RowItem = {
  discipline: string; // "E" | "V" | ...
  validationId?: string; // "standardRoom.E"
  decisionId?: string; // "decision:standardRoom.E"
};

export const RFPBar: React.FC = () => {
  const dispatch = useAppDispatch();

  const standardRoomValidations = useAppSelector(selectStandardRoomValidationColorItems);
  const decisionFilters = useAppSelector(selectValidationDecisionColorItems);

  const activeColorPanelItem = useAppSelector((state) => state.design.activeColorPanelItem);

  const rows: RowItem[] = useMemo(() => {
    const map = new Map<string, RowItem>();

    for (const v of standardRoomValidations) {
      const discipline = getDisciplineFromStandardRoomKey(v.id);
      if (!discipline) continue;
      const existing = map.get(discipline) ?? { discipline };
      existing.validationId = v.id;
      map.set(discipline, existing);
    }

    for (const d of decisionFilters) {
      const discipline = getDisciplineFromDecisionFilterId(d.id);
      if (!discipline) continue;
      const existing = map.get(discipline) ?? { discipline };
      existing.decisionId = d.id;
      map.set(discipline, existing);
    }

    return Array.from(map.values()).sort((a, b) => a.discipline.localeCompare(b.discipline));
  }, [standardRoomValidations, decisionFilters]);

  useEffect(() => {
    if (!activeColorPanelItem) {
      const first = rows[0];
      const defaultId = first?.decisionId ?? first?.validationId;
      if (defaultId) dispatch(setActiveColorPanelItemInRedux(defaultId));
    }
  }, [dispatch, rows, activeColorPanelItem]);

  return (
    <ModuleToolBarShell>
      <Group align="center" gap="md">
        <Group gap="sm" align="center">
          <Text fw={600} size="sm">
            RFP-validering
          </Text>
          <Divider orientation="vertical" h={24} />
          <Text size="sm" c="dimmed">
            Standardrom-valideringer
          </Text>
        </Group>

        <Group gap="sm" align="center">
          {rows.map((row) => {
            const poengActive = row.validationId && activeColorPanelItem === row.validationId;
            const beslutningActive = row.decisionId && activeColorPanelItem === row.decisionId;

            return (
              <Group key={row.discipline} gap={6} align="center">
                <Tooltip
                  label={`Disiplin: ${row.discipline}`}
                  position="bottom"
                  withArrow
                  openDelay={250}
                >
                  <Text size="sm" fw={700} style={{ minWidth: 18, textAlign: "center" }}>
                    RI{row.discipline}
                  </Text>
                </Tooltip>

                <Tooltip
                  label="Poeng: viser farge basert på hvor godt rommet matcher standardrommet (matchrate)."
                  position="bottom"
                  withArrow
                  openDelay={250}
                >
                  <span>
                    <Button
                      size="sm"
                      radius="xl"
                      variant={poengActive ? "filled" : "subtle"}
                      color="blue"
                      disabled={!row.validationId}
                      onClick={() =>
                        row.validationId &&
                        dispatch(setActiveColorPanelItemInRedux(row.validationId))
                      }
                      style={{ paddingLeft: 14, paddingRight: 14, fontWeight: 600 }}
                    >
                      Poeng
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip
                  label="Beslutning: viser farge basert på manuell status (Godkjent / Godkjent med avvik / Må kontrolleres)."
                  position="bottom"
                  withArrow
                  openDelay={250}
                >
                  <span>
                    <Button
                      size="sm"
                      radius="xl"
                      variant={beslutningActive ? "filled" : "subtle"}
                      color="blue"
                      disabled={!row.decisionId}
                      onClick={() =>
                        row.decisionId && dispatch(setActiveColorPanelItemInRedux(row.decisionId))
                      }
                      style={{ paddingLeft: 14, paddingRight: 14, fontWeight: 600 }}
                    >
                      Beslutning
                    </Button>
                  </span>
                </Tooltip>

                <Divider orientation="vertical" h={24} />
              </Group>
            );
          })}
        </Group>
      </Group>
    </ModuleToolBarShell>
  );
};

// "standardRoom.E" -> "E"
const getDisciplineFromStandardRoomKey = (key: string): string | null => {
  const idx = key.indexOf(".");
  if (idx === -1 || idx + 1 >= key.length) return null;
  return key.slice(idx + 1);
};

// "decision:standardRoom.E" -> "E"
const getDisciplineFromDecisionFilterId = (id: string): string | null => {
  const prefixIdx = id.indexOf(":");
  const withoutPrefix = prefixIdx >= 0 ? id.slice(prefixIdx + 1) : id;
  const idx = withoutPrefix.indexOf(".");
  if (idx === -1 || idx + 1 >= withoutPrefix.length) return null;
  return withoutPrefix.slice(idx + 1);
};
